import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import auth from '../config/firebase';

const LoginDialog = ({ open, onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [step, setStep] = useState(1); // 1: Phone input, 2: OTP input, 3: Name input (for new users)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [requiresRegistration, setRequiresRegistration] = useState(false);

  const { login } = useAuth();

  // Initialize reCAPTCHA
  const setupRecaptcha = () => {
    if (!recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container-dialog', {
        size: 'normal',
        callback: (response) => {
          console.log('✅ reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.warn('⚠️  reCAPTCHA expired');
          setError('reCAPTCHA expired. Please try again.');
        }
      });
      
      setRecaptchaVerifier(verifier);
      return verifier;
    }
    return recaptchaVerifier;
  };

  // Format phone number to E.164 format
  const formatPhoneNumber = (phone) => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // If exactly 10 digits, add India country code
    if (cleaned.length === 10) {
      cleaned = '91' + cleaned;
    }
    // If 12 digits and starts with 91, it already has country code
    // If other lengths, use as-is (might be invalid but let Firebase validate)
    
    return '+' + cleaned;
  };

  // Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!phoneNumber || phoneNumber.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!auth) {
      setError('Firebase authentication not initialized');
      return;
    }

    setLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('📱 Sending OTP to:', formattedPhone);

      const verifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      
      setConfirmationResult(result);
      setStep(2);
      setSuccess('OTP sent successfully! Please check your phone.');
      console.log('✅ OTP sent successfully');
    } catch (error) {
      console.error('❌ Error sending OTP:', error);
      setError(error.message || 'Failed to send OTP. Please try again.');
      
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
        setRecaptchaVerifier(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP and Login
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 Step 1: Verifying OTP with Firebase...');
      
      const userCredential = await confirmationResult.confirm(otp);
      const user = userCredential.user;
      
      console.log('✅ Step 2: Firebase authentication successful:', user.phoneNumber);

      console.log('🔑 Step 3: Getting Firebase ID token...');
      const idToken = await user.getIdToken();
      console.log('✅ Step 4: ID token received');

      console.log('📡 Step 5: Sending to backend /api/auth/firebase/verify...');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('⏱️  Backend request TIMEOUT after 30 seconds');
        controller.abort();
      }, 30000);

      let response;
      try {
        const startTime = Date.now();
        response = await fetch('/api/auth/firebase/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            idToken,
            phoneNumber: user.phoneNumber
          }),
          signal: controller.signal
        });
        const endTime = Date.now();
        console.log(`⏱️  Backend responded in ${endTime - startTime}ms`);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.error('❌ Fetch error:', fetchError);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout. Server is taking too long (>30s). Please check your connection and try again.');
        }
        throw new Error('Network error. Please check your internet connection.');
      }

      clearTimeout(timeoutId);
      console.log('✅ Step 6: Backend response received, status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('📦 Step 7: Response data:', { 
          success: data.success, 
          requiresRegistration: data.requiresRegistration,
          message: data.message 
        });
      } catch (jsonError) {
        console.error('❌ Failed to parse JSON response:', jsonError);
        throw new Error('Invalid response from server');
      }

      if (data.requiresRegistration) {
        console.log('📝 New user detected - requesting name');
        setFirebaseUser(user);
        setRequiresRegistration(true);
        setStep(3);
        setSuccess('Please provide your name to complete registration');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        console.error('❌ Response not OK:', response.status, data.message);
        throw new Error(data.message || 'Login failed');
      }

      if (data.success) {
        console.log('✅ Step 8: Login successful, storing tokens...');
        login(data.data.accessToken, data.data.refreshToken, data.data.user);
        
        setSuccess('Login successful!');
        setLoading(false);
        
        console.log('🚪 Closing dialog in 1 second...');
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        console.error('❌ Login not successful:', data.message);
        throw new Error(data.message || 'Login failed');
      }

    } catch (error) {
      console.error('❌ Error in handleVerifyOTP:', error);
      console.error('Error stack:', error.stack);
      setLoading(false);
      
      let errorMessage = 'Invalid OTP. ';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage += 'Please check and try again.';
      } else if (error.code === 'auth/code-expired') {
        errorMessage += 'The code has expired. Please request a new one.';
      } else {
        errorMessage += error.message || 'Please try again.';
      }
      
      setError(errorMessage);
    }
  };

  // Complete registration with firstName and lastName
  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstName || !lastName) {
      setError('Please enter both first name and last name');
      return;
    }

    setLoading(true);

    try {
      console.log('📝 Completing registration with name...');

      const idToken = await Promise.race([
        firebaseUser.getIdToken(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token request timed out')), 10000)
        )
      ]);

      const response = await Promise.race([
        fetch('/api/auth/firebase/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            idToken,
            phoneNumber: firebaseUser.phoneNumber,
            firstName: firstName.trim(),
            lastName: lastName.trim()
          })
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timed out')), 15000)
        )
      ]);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      if (data.success) {
        login(data.data.accessToken, data.data.refreshToken, data.data.user);
        
        setSuccess('Registration successful!');
        
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error(data.message || 'Registration failed');
      }

    } catch (error) {
      console.error('❌ Error completing registration:', error);
      if (error.message.includes('timed out') || error.message.includes('Timeout')) {
        setError('Request timed out. Please check your internet connection and try again.');
      } else {
        setError(error.message || 'Failed to complete registration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStep(1);
    setOtp('');
    setFirstName('');
    setLastName('');
    setError('');
    setSuccess('');
    setFirebaseUser(null);
    setRequiresRegistration(false);
    if (recaptchaVerifier) {
      recaptchaVerifier.clear();
      setRecaptchaVerifier(null);
    }
  };

  const handleClose = () => {
    handleReset();
    setPhoneNumber('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#7f4b2d', color: '#fff' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {step === 1 ? 'Phone Login' : step === 2 ? 'Verify OTP' : 'Complete Registration'}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {step === 1 && (
          <form onSubmit={handleSendOTP}>
            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter 10-digit mobile number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText="Enter your registered mobile number"
            />

            <Box id="recaptcha-container-dialog" sx={{ my: 2 }} />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !phoneNumber}
              sx={{ 
                mt: 2,
                bgcolor: '#7f4b2d',
                '&:hover': { bgcolor: '#001040' }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerifyOTP}>
            <Alert severity="info" sx={{ mb: 2 }}>
              OTP sent to {phoneNumber}
            </Alert>

            <TextField
              fullWidth
              label="Enter OTP"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              margin="normal"
              required
              autoFocus
              inputProps={{ maxLength: 6 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || otp.length !== 6}
              sx={{ 
                mt: 3,
                bgcolor: '#7f4b2d',
                '&:hover': { bgcolor: '#001040' }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Login'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleReset}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              Change Phone Number
            </Button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleCompleteRegistration}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Welcome! Please provide your name to complete registration.
            </Alert>

            <TextField
              fullWidth
              label="First Name"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText="Enter your first name (in Telugu or English)"
            />

            <TextField
              fullWidth
              label="Last Name"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="normal"
              required
              helperText="Enter your last name (in Telugu or English)"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !firstName || !lastName}
              sx={{ 
                mt: 3,
                bgcolor: '#7f4b2d',
                '&:hover': { bgcolor: '#001040' }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Complete Registration'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
